import { Injectable } from '@angular/core';
import { Observable, Subject, fromEvent } from 'rxjs';

export interface OfflineRequestRow {
	date: Date,
	label: string;
	data: string; // JSON encoded
}

@Injectable({
  	providedIn: 'root'
})
export class OfflineService {

	private _IndexedDb: IDBFactory;
	private _dbName = 'offline';
	private _dbVersion = 2;
	private _db: IDBDatabase;
	private _storeName = 'requests';
	private _volatileStoreName = 'volatile';

	public ready = new Subject<boolean>();

  	constructor() {
		this._IndexedDb = indexedDB;
		this._init();

		// console.warn('temp hack - trash store on unload');
		// window.addEventListener('unload', () => {
		// 	this._IndexedDb.deleteDatabase(this._dbName);
		// })
	}

	dateAsString(d: Date) {
		return `${d.getFullYear()}/${d.getMonth()}/${d.getDate()}`;
	}

	/**
	 * @description saves the request/response pair in the table
	 * @param req JSON encoded request object being sent to the server
	 * @param resp RAW data received
	 * @returns Promise that is null on suceess, or ANY if an error occured.
	 */
	saveVolatile(key: string, data: any) {
		return this._save(key, data, this._volatileStoreName);
	}

	save(key: string, data: any) {
		return this._save(key, data, this._storeName);
	}

	private _save(key: string, data: any, storeName: string) {

		return new Promise(res => {

			const db = this._db,
				trans = db.transaction(storeName, 'readwrite');

			let store: IDBObjectStore;

			trans.addEventListener('complete', () => res(false));

			trans.addEventListener('error', (evt) => {
				console.warn('error', evt);
				res('transaction_failed');
			});

			store = trans.objectStore(storeName);

			if (store.get(key)) {
				store.put(data, key);
			} else {
				store.add(data, key);
			}
		});
	}

	get<T>(key: string): Observable<T> {
		return this._get<T>(key, this._storeName);
	}

	getVolatile<T>(key: string): Observable<T> {
		return this._get<T>(key, this._volatileStoreName);
	}

	private _get<T>(key: string, storeName: string): Observable<T> {

		return new Observable(obs => {
			
			const db = this._db,
				trans = db ? db.transaction(storeName, 'readonly') : false;

			if (!trans) {
				obs.error('no_db_available');
				return;
			};

			const fetch = trans
				.objectStore(storeName)
				.get(key);

			fetch.addEventListener('success', (evt: any) => {
				const r = evt.target.result;
				if (r) {
					obs.next(r);
				} else {
					obs.error('cached_not_available');
				}
				obs.complete();
			});

			fetch.addEventListener('error', () => {
				console.warn('an error occured');
				obs.next(null);
				obs.complete();
			});
				
		})
	}

	private _init() {
		
		const req = this._IndexedDb.open(this._dbName ,this._dbVersion);

		req.addEventListener('upgradeneeded', (evt) => this._upgradeNeeded(evt))

		req.addEventListener('success', (evt: any) => {
			this._db = evt.target.result;
			console.log('DB is ready');
			this.ready.next(true);
		});

		req.addEventListener('error', (evt: any) => {
			console.warn('an error occured spinning up the offline stuffs');
			this.ready.next(false);
		});

		req.onupgradeneeded
	}

	private _upgradeNeeded(evt: any) {

		const db: IDBDatabase = evt.target.result;

		switch (evt.oldVersion || 0) {

			case 0:
				db.createObjectStore(this._storeName, {autoIncrement: true});
				console.log(`created store: ${this._storeName}`);

			case 1:
				// nothing.. continue
			case 2:
				db.createObjectStore(this._volatileStoreName, {autoIncrement: true});
				console.log(`created volatile store: ${this._volatileStoreName}`);

			default:
				console.log(`finished updating to version ${evt.newVersion}`);
		}
	}
}
